import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FaWallet, FaHistory, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const FaClock = () => <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>;

const Payments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (!user || !user.id) { navigate('/login'); return; }
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_payments.php?user_id=${user.id}`);
      const result = await response.json();
      if (result.status === 'success') setHistory(result.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !user.id) return null;

  return (
    <DashboardLayout title="Payments">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">

        {/* Left: History & Summary */}
        <div className="xl:col-span-2 space-y-4 sm:space-y-6">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-7 text-white shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <FaWallet className="text-orange-500 text-xl sm:text-2xl" />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] opacity-50">Overview</span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:gap-8">
              <div>
                <p className="text-[9px] sm:text-xs font-bold text-slate-400 mb-0.5">Enrolled Months</p>
                <h3 className="text-2xl sm:text-3xl font-black">{history.filter(h => h.status === 'approved').length}</h3>
              </div>
              <div>
                <p className="text-[9px] sm:text-xs font-bold text-slate-400 mb-0.5">Pending Slips</p>
                <h3 className="text-2xl sm:text-3xl font-black text-orange-500">{history.filter(h => h.status === 'pending').length}</h3>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-50 flex items-center gap-2">
              <FaHistory className="text-orange-500 text-sm" />
              <h2 className="text-sm font-bold text-slate-800">Payment History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[360px]">
                <thead className="bg-slate-50">
                  <tr className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="py-3 px-4">Month / Course</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="py-10 text-center text-slate-400 font-bold text-xs">Loading...</td></tr>
                  ) : history.length === 0 ? (
                    <tr><td colSpan="3" className="py-10 text-center text-slate-400 font-bold italic text-xs">No payment history found.</td></tr>
                  ) : (
                    history.map((pay) => (
                      <tr key={pay.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-bold text-slate-700 text-[10px] sm:text-xs">{pay.month_name || 'Full Course'}</p>
                          <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold uppercase">{pay.course_title}</p>
                        </td>
                        <td className="py-3 px-4 font-black text-slate-600 text-[10px] sm:text-xs">LKR {pay.amount}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${
                            pay.status === 'approved' ? 'bg-green-50 text-green-600' :
                            pay.status === 'pending' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'
                          }`}>
                            {pay.status === 'approved' ? <FaCheckCircle size={8} /> : pay.status === 'pending' ? <FaClock /> : <FaExclamationCircle size={8} />}
                            {pay.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Bank Details */}
        <div>
          <div className="bg-white rounded-xl border border-slate-100 shadow-lg p-4 sm:p-6 lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-sm sm:text-base font-black text-slate-800 mb-0.5">Bank Details</h2>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Transfer your payment here</p>
              </div>
              <img src="https://www.lankahost.lk/Content/ux/images/Bank_of_Ceylon.svg.png" alt="BOC Logo" className="h-9 sm:h-11 object-contain" />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
              <div>
                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Account Number</span>
                <p className="text-xl sm:text-2xl font-black text-orange-500 tracking-wider">95861437</p>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Account Name</span>
                <p className="text-xs font-black text-slate-800 uppercase mt-0.5">W.G Kasun Manoj Kumarasinghe</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Bank</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5">BOC</p>
                </div>
                <div>
                  <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Branch</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5">Polgahawela</p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-[9px] text-blue-600 font-bold leading-relaxed">
                * After transferring, go to <strong>Courses</strong> → select your month → upload payment slip to unlock materials.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Payments;
